import { cn } from "@/common/helpers/utils";
import { Button, ButtonSize } from "@/components/ui/button";
import { Input, InputTextAlign } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface PageInputPopoverProps {
  currentPage: number;
  lastPage: number;
  onGoToPage: (page: number) => void;
  isLoading?: boolean;
  className?: string;
}

export const PageInputPopover = ({
  currentPage,
  lastPage,
  onGoToPage,
  isLoading,
  className,
}: PageInputPopoverProps) => {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [pendingPage, setPendingPage] = useState<number | null>(null);
  const [prevIsLoading, setPrevIsLoading] = useState(isLoading);

  const maxDigits = Math.max(String(lastPage).length, 1);
  const isPending = pendingPage !== null;
  const parsedDraft = Number(draft);
  const canCommit = draft !== "" && Number.isInteger(parsedDraft) && !isPending;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    setDraft(nextOpen ? String(currentPage) : "");
    setPendingPage(null);
  };

  const jumpResolved = isPending && !isLoading && (currentPage === pendingPage || prevIsLoading);
  if (open && jumpResolved) {
    setOpen(false);
  }

  if (prevIsLoading !== isLoading) {
    setPrevIsLoading(isLoading);
  }

  const handleDraftChange = (value: string) => {
    const digits = value.replace(/\D/g, "");
    if (digits === "") {
      setDraft("");
      return;
    }

    const clamped = Math.min(Math.max(Number(digits), 1), lastPage);
    setDraft(String(clamped));
  };

  const commitDraft = () => {
    if (!canCommit) {
      return;
    }

    const page = Math.min(Math.max(parsedDraft, 1), lastPage);
    if (page === currentPage) {
      handleOpenChange(false);
      return;
    }

    onGoToPage(page);
    if (isLoading === undefined) {
      handleOpenChange(false);
      return;
    }
    setPendingPage(page);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitDraft();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("GoToPageAria")}
          className={cn(
            "inline-flex items-center justify-center gap-1 whitespace-nowrap tabular-nums sm:gap-1.5",
            "h-7 rounded-md px-2 text-xs font-medium sm:px-3 sm:text-sm",
            "hover:bg-muted/60 cursor-pointer transition-colors",
            className,
          )}
        >
          {isLoading ? (
            <Skeleton className="h-3.5" style={{ width: `${String(currentPage).length}ch` }} />
          ) : (
            <span className="text-foreground font-bold">{currentPage}</span>
          )}
          <span className="text-muted-foreground text-xs">{t("Of")}</span>
          {isLoading ? (
            <Skeleton className="h-3.5" style={{ width: `${maxDigits}ch` }} />
          ) : (
            <span className="text-muted-foreground font-medium">{lastPage}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-48 p-2">
        <div className="flex items-center gap-1.5">
          <Input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            aria-label={t("GoToPageAria")}
            placeholder={t("EnterPageNumber")}
            value={draft}
            maxLength={maxDigits}
            disabled={isPending}
            textAlign={InputTextAlign.Center}
            onChange={(event) => handleDraftChange(event.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={(event) => event.currentTarget.select()}
            className="flex-1"
            inputClassName="h-8 tabular-nums"
          />
          <Button
            type="button"
            size={ButtonSize.Icon}
            aria-label={t("GoToPageAria")}
            onClick={commitDraft}
            disabled={!canCommit}
            className="h-8 w-8 shrink-0"
          >
            {isPending ? (
              <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />
            ) : (
              <ArrowRight className="h-4 w-4" />
            )}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
